import { useMemo} from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { E3M3_TO_MCF, M3_TO_BBL } from '../../helpers/conversionFactors';
import { selectUser } from '../../reducers/userSlice';
import { fbaConversionFactors } from './fbaConversionFactors';
import { selectFBA } from '../../reducers/fbaSlice';

export const FBAAnalyticsChart = ({ displayFBACases=[], fbaUnits='field', normalizeRate }) => {

  const user = useSelector(selectUser);
  const fba = useSelector(selectFBA);

  const darkMode = user?.value?.darkMode;

  const data = useMemo(() => {
    return getChartData(displayFBACases);

    function getChartData(displayFBACases) {
      try {

        let traces = [];

        for (const fbaId of displayFBACases) {
          const fbaCase = fba?.byId?.[fbaId];
          const fbaData = fbaCase?.fbaData;

          if (!fbaData) continue;

          const fbaName = fbaCase?.name?.substring(0, 20);
          const hasOil = fbaData?.reduce((tot, curr) => tot + curr?.oil, 0) > 1 ? true : false;
          const hasWellboreModel = fbaCase?.fbaWellboreModel && Object.keys(fbaCase?.fbaWellboreModel);
          const pfi = Math.max(...fbaData?.map(d => (hasWellboreModel ? d?.pwf : d?.pwh))) * 1.05;
          // const color = fbaId === fba?.currentFBAId ? colors?.[0] : colors?.[1 + Math.floor(Math.random() * (colors?.length - 1))];
          const color = fbaCase?.color;

          if (!fbaData?.length > 0) return [];
    
          // add traces
          let oilProductionTrace = {
            x: [],
            y: [],
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: fbaName,
            line: {
              width: 2,
              color
            },
            legendgroup: fbaName,
            showlegend: hasOil
          };
          let gasProductionTrace = {
            x: [],
            y: [],
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x2',
            yaxis: 'y2',
            name: fbaName,
            line: {
              width: 2,
              color
            },
            legendgroup: fbaName,
            showlegend: !hasOil
          };
          let waterProductionTrace = {
            x: [],
            y: [],
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x3',
            yaxis: 'y3',
            name: fbaName,
            line: {
              width: 2,
              color
            },
            legendgroup: fbaName,
            showlegend: false
          };
          let pressureTrace = {
            x: [],
            y: [],
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x4',
            yaxis: 'y4',
            name: fbaName,
            line: {
              width: 2,
              color
            },
            legendgroup: fbaName,
            showlegend: false
          };
         
          fbaData?.forEach(d => {
            let oilRate = (hasOil ? d.oil : d.condensate) * (fbaUnits === 'metric' ? 1 / M3_TO_BBL : 1);
            let gasRate = d.gas * (fbaUnits === 'metric' ? 1 / E3M3_TO_MCF : 1);
            let waterRate = d.water * (fbaUnits === 'metric' ? 1 / M3_TO_BBL : 1);

            const pressure = hasWellboreModel ? d.pwf : d.pwh;

            if (normalizeRate > 1) { // if rate normalization is toggled
              let normFactor = pfi - pressure; // in psi

              if (fbaUnits === 'metric') {
                normFactor = normFactor * fbaConversionFactors.pwh;
              };

              if (normFactor > 0) {
                oilRate = oilRate / normFactor;
                gasRate = gasRate / normFactor;
                waterRate = waterRate / normFactor;
              };
            };

            oilProductionTrace.x.push(d.days);
            oilProductionTrace.y.push(oilRate);
            gasProductionTrace.x.push(d.days);
            gasProductionTrace.y.push(gasRate);
            waterProductionTrace.x.push(d.days);
            waterProductionTrace.y.push(waterRate);
            pressureTrace.x.push(d.days);
            pressureTrace.y.push((hasWellboreModel ? d.pwf : d.pwh) * (fbaUnits === 'metric' ? fbaConversionFactors.pwh : 1));
          });

          traces = [...traces, oilProductionTrace, gasProductionTrace, waterProductionTrace, pressureTrace];
        };
        
        return traces;

      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [displayFBACases, fba?.byId, fbaUnits, normalizeRate]);


  const layout = {
    grid: {rows: 2, columns: 2, pattern: 'independent'},
    yaxis: { // Oil rate y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `${normalizeRate > 1 ? 'Normalized' : ''} Oil/Condensate Rate (${fbaUnits === 'metric' ? 'm3' : 'stb'}/d${normalizeRate > 1 ? (fbaUnits === 'metric' ? '/kPa' : '/psi') : ''})`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.55, 1],
    },
    xaxis: { // Oil rate x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0, 0.45],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis2: { // Gas rate y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `${normalizeRate > 1 ? 'Normalized' : ''} Gas Rate (${fbaUnits === 'metric' ? 'E3m3' : 'mscf'}/d${normalizeRate > 1 ? (fbaUnits === 'metric' ? '/kPa' : '/psi') : ''})`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.55, 1],
    },
    xaxis2: { // Gas rate x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0.55, 1],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis3: { // Water rate y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `${normalizeRate > 1 ? 'Normalized' : ''} Water Rate (${fbaUnits === 'metric' ? 'm3' : 'stb'}/d${normalizeRate > 1 ? (fbaUnits === 'metric' ? '/kPa' : '/psi') : ''})`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
    },
    xaxis3: { // water rate x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0, 0.45],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis4: { // BHP y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Pwf (${fbaUnits === 'metric' ? 'kPa' : 'psi'})`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
    },
    xaxis4: { // BHP x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0.55, 1],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      orientation: "h",
      yanchor: "top",
      yref: 'paper',
      y: -0.05,
    },
    uirevision:'true',
    annotations: [
      {
        text: `Oil/Condensate Rate`,
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        xanchor: 'center',
        x: 0.25, //position in x domain
        y: 1.025, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Gas Rate`,
          font: {
          size: 14,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        xanchor: 'center',
        x: 0.75, //position in x domain
        y: 1.025, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Water Rate`,
          font: {
          size: 14,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        xanchor: 'center',
        x: 0.25, //position in x domain
        y: 0.475, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: "Bottomhole Flowing Pressure",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        xanchor: 'center',
        x: 0.75, //position in x domain
        y: 0.475, //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    margin: {"t": 50, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.025,
        y: 1.015,
        sizex: 0.1,
        sizey: 0.0167,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_${darkMode ? 'white' : 'primary'}.png`,
        xref: "paper",
        yref: "paper",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "FBA analytics-" + (new Date().toISOString().split('T')[0]),
      height: 1440,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );
};
